<template>
  <rmr-modal
    :value="value"
    variant="form"
    :icon="icons.mdiCalendarClock"
    eyebrow="Publication demandée"
    title="Reprogrammer l’article"
    :subtitle="article ? article.productTitle : ''"
    max-width="560"
    @input="$emit('input', $event)"
  >
    <template #body>
      <v-card-text class="ctc-reschedule">
        <p class="ctc-reschedule__help">
          La date demandée est dépassée. Choisissez une nouvelle publication :
          la génération sera relancée aussitôt.
        </p>
        <rmr-segmented-control
          v-model="postStatus"
          :items="postStatusOptions"
          size="md"
          block
          aria-label="Mode de publication dans le CMS"
        />
        <div v-if="postStatus === 'future'" class="ctc-reschedule__schedule">
          <rmr-date-picker-field
            v-model="scheduledDate"
            close-on-input
            label="Date"
            prepend-inner-icon="mdi-calendar"
            :picker-props="{ min: today, firstDayOfWeek: 1 }"
          />
          <rmr-select
            v-model="scheduledTime"
            :items="timeOptions"
            label="Heure"
            prepend-inner-icon="mdi-clock-outline"
            :menu-props="{ maxHeight: 260 }"
          />
        </div>
        <span v-if="scheduleError" class="ctc-reschedule__error" role="alert">
          {{ scheduleError }}
        </span>
        <span
          v-else-if="postStatus !== 'draft'"
          class="ctc-reschedule__warning"
        >
          L’article sera mis en ligne sans relecture préalable.
        </span>
      </v-card-text>
    </template>
    <template #footer>
      <rmr-action-button
        label="Annuler"
        variant="ghost"
        @click="$emit('input', false)"
      />
      <rmr-action-button
        label="Enregistrer et relancer"
        :icon="icons.mdiRefresh"
        :loading="saving"
        :disabled="!!scheduleError"
        @click="save"
      />
    </template>
  </rmr-modal>
</template>

<script>
import { computed, ref, watch } from "@vue/composition-api";
import { mdiCalendarClock, mdiRefresh } from "@mdi/js";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrSegmentedControl from "@/components/ui/actions/RmrSegmentedControl.vue";
import RmrDatePickerField from "@/components/ui/forms/RmrDatePickerField.vue";
import RmrSelect from "@/components/ui/forms/RmrSelect.vue";
import RmrModal from "@/components/ui/layout/RmrModal.vue";

import { POST_STATUS_OPTIONS } from "../constants";
import { localDateKey, scheduleTimeOptions } from "../ctc-model";

export default {
  name: "CtcRescheduleModal",
  components: {
    RmrActionButton,
    RmrDatePickerField,
    RmrModal,
    RmrSegmentedControl,
    RmrSelect,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    // The draft to reschedule (GET /articles item).
    article: {
      type: Object,
      default: null,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const postStatus = ref("future");
    const scheduledDate = ref(null);
    const scheduledTime = ref(null);
    const today = computed(() => localDateKey(new Date()));

    watch(
      () => props.value,
      (open) => {
        if (!open) return;
        postStatus.value = props.article?.postStatus || "future";
        scheduledDate.value = null;
        scheduledTime.value = null;
      }
    );

    const scheduledAt = computed(() => {
      if (!scheduledDate.value || !scheduledTime.value) return null;
      const date = new Date(`${scheduledDate.value}T${scheduledTime.value}:00`);
      return Number.isNaN(date.getTime()) ? null : date;
    });

    const scheduleError = computed(() => {
      if (postStatus.value !== "future") return null;
      if (!scheduledAt.value) {
        return "Indiquez la date et l’heure de publication.";
      }
      if (scheduledAt.value.getTime() <= Date.now()) {
        return "La date de publication doit être dans le futur.";
      }
      return null;
    });

    const save = () => {
      if (scheduleError.value || !props.article) return;
      emit("save", {
        id: props.article.id,
        postStatus: postStatus.value,
        scheduledAt:
          postStatus.value === "future"
            ? scheduledAt.value.toISOString()
            : null,
      });
    };

    return {
      icons: { mdiCalendarClock, mdiRefresh },
      postStatus,
      postStatusOptions: POST_STATUS_OPTIONS,
      save,
      scheduledDate,
      scheduledTime,
      scheduleError,
      timeOptions: computed(() =>
        scheduleTimeOptions(scheduledDate.value, scheduledTime.value)
      ),
      today,
    };
  },
};
</script>

<style lang="scss" scoped>
.ctc-reschedule {
  display: grid;
  gap: 14px;
}

.ctc-reschedule__help {
  margin: 0;
  color: $rmr-text-muted;
  font-size: $rmr-fs-sm;
}

.ctc-reschedule__schedule {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.ctc-reschedule__error {
  color: $rmr-danger;
  font-size: $rmr-fs-sm;
}

.ctc-reschedule__warning {
  color: $rmr-text-muted;
  font-size: $rmr-fs-sm;
}

@media (max-width: 599px) {
  .ctc-reschedule__schedule {
    grid-template-columns: 1fr;
  }
}
</style>
